import React, { useEffect, useCallback, useState } from 'react';
import { useQuestionnaire } from '../../lib/QuestionnaireContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/Card';
import { Button } from '../ui/Button';
import { useNavigate } from 'react-router-dom';
import { 
  Table, 
  TableHeader, 
  TableBody, 
  TableRow, 
  TableHead, 
  TableCell 
} from '../ui/table';
import { SortableHeader } from '../ui/sortable-header';
import { formatDate } from '../../lib/utils';
import { deleteQuestionnaireDoc } from '../../lib/firebase';
import { Skeleton } from '../ui/Skeleton';
import { convertTemplateToSteps } from '../../lib/templateUtils';

type SortField = 'date' | 'name' | 'email' | 'company' | 'projectCategories' | 'status';
type SortDirection = 'asc' | 'desc';

const AdminQuestionnaires: React.FC = () => {
  const { allQuestionnaires, loading, loadAllQuestionnaires, isAdmin, activeTemplate } = useQuestionnaire();
  const navigate = useNavigate();
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const loadQuestionnaires = useCallback(() => {
    console.log("Admin: Loading all questionnaires, isAdmin=", isAdmin);
    loadAllQuestionnaires();
  }, [isAdmin, loadAllQuestionnaires]);

  // Calculate completion percentage for a questionnaire
  const getCompletionPercentage = (questionnaire: any) => {
    if (questionnaire.isComplete) {
      return 100;
    }

    if (!activeTemplate) {
      return 0;
    }

    try {
      const steps = convertTemplateToSteps(activeTemplate, questionnaire.formData || {});
      const totalSteps = steps.length;
      const currentStep = Math.max(0, questionnaire.currentStep || 0);
      
      if (totalSteps === 0) return 0;
      
      return Math.round((currentStep / totalSteps) * 100);
    } catch (error) {
      console.error('Error calculating completion percentage:', error);
      return 0;
    }
  };

  // Circular progress component
  const CircularProgress: React.FC<{ percentage: number; isComplete: boolean }> = ({ percentage, isComplete }) => {
    const radius = 16;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - (percentage / 100) * circumference;

    return (
      <div className="relative w-10 h-10 flex items-center justify-center">
        <svg
          className="w-10 h-10 transform -rotate-90"
          width="40"
          height="40"
        >
          <circle
            cx="20"
            cy="20"
            r={radius}
            stroke="currentColor"
            strokeWidth="3"
            fill="none"
            className="text-gray-200"
          />
          <circle
            cx="20"
            cy="20"
            r={radius}
            stroke="currentColor"
            strokeWidth="3"
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            className={isComplete ? "text-green-500" : "text-blue-500"}
            style={{
              transition: 'stroke-dashoffset 0.5s ease-in-out',
            }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`text-xs font-medium ${isComplete ? 'text-green-600' : 'text-gray-700'}`}>
            {isComplete ? '✓' : `${percentage}%`}
          </span>
        </div>
      </div>
    );
  };

  // Single useEffect to load questionnaires when component mounts or isAdmin changes
  useEffect(() => {
    console.log("Admin: Component mounted or isAdmin changed, loading questionnaires");
    loadQuestionnaires();
  }, [isAdmin, loadQuestionnaires]);
  
  // Removed the second useEffect that was causing the infinite loop

  const viewQuestionnaireDetails = (userId: string | undefined, questionnaireId: string) => {
    if (!userId) {
      console.error('User ID is undefined for questionnaire:', questionnaireId);
      return;
    }
    navigate(`/questionnaire/admin/view/${userId}/${questionnaireId}`);
  };

  const handleRefresh = () => {
    console.log("Manually refreshing questionnaires");
    loadQuestionnaires();
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to descending for date, ascending for others
      setSortField(field);
      setSortDirection(field === 'date' ? 'desc' : 'asc');
    }
  };

  const handleDeleteQuestionnaire = async (userId: string | undefined, questionnaireId: string) => {
    if (!userId) {
      console.error('User ID is undefined for questionnaire:', questionnaireId);
      return;
    }
    
    if (window.confirm('Are you sure you want to delete this questionnaire? This action cannot be undone.')) {
      try {
        setDeletingId(questionnaireId);
        await deleteQuestionnaireDoc(userId, questionnaireId);
        // Refresh the questionnaires list
        await loadQuestionnaires();
      } catch (error) {
        console.error('Error deleting questionnaire:', error);
        alert('Error deleting questionnaire. Please try again.');
      } finally {
        setDeletingId(null);
      }
    }
  };

  const sortedQuestionnaires = [...allQuestionnaires].sort((a, b) => {
    let comparison = 0;

    switch (sortField) {
      case 'date':
        const dateA = a.submittedAt || a.updatedAt || a.createdAt || new Date(0);
        const dateB = b.submittedAt || b.updatedAt || b.createdAt || new Date(0);
        comparison = dateA && dateB ? dateA.getTime() - dateB.getTime() : 0;
        break;
      case 'name':
        comparison = (a.formData.name || '').localeCompare(b.formData.name || '');
        break;
      case 'email':
        comparison = (a.formData.email || '').localeCompare(b.formData.email || '');
        break;
      case 'company':
        comparison = (a.formData.company || '').localeCompare(b.formData.company || '');
        break;
      case 'projectCategories':
        // Sort by the first category or empty string if no categories
        const categoriesA = Array.isArray(a.formData.projectCategories) ? a.formData.projectCategories.join(', ') : '';
        const categoriesB = Array.isArray(b.formData.projectCategories) ? b.formData.projectCategories.join(', ') : '';
        comparison = categoriesA.localeCompare(categoriesB);
        break;
      case 'status':
        // Sort by completion status first, then by step number
        if (a.isComplete === b.isComplete) {
          comparison = (a.currentStep || 0) - (b.currentStep || 0);
        } else {
          comparison = a.isComplete ? 1 : -1;
        }
        break;
      default:
        comparison = 0;
    }

    // Reverse for descending order
    return sortDirection === 'desc' ? -comparison : comparison;
  });

  // Skeleton rows for loading state
  const renderSkeletonRows = () => {
    return Array(5).fill(0).map((_, index) => (
      <TableRow key={`skeleton-${index}`}>
        <TableCell>
          <Skeleton className="h-6 w-20" />
        </TableCell>
        <TableCell>
          <Skeleton className="h-4 w-24" />
        </TableCell>
        <TableCell>
          <Skeleton className="h-4 w-32" />
        </TableCell>
        <TableCell>
          <Skeleton className="h-4 w-40" />
        </TableCell>
        <TableCell>
          <Skeleton className="h-4 w-36" />
        </TableCell>
        <TableCell>
          <Skeleton className="h-4 w-28" />
        </TableCell>
        <TableCell>
          <div className="flex space-x-2">
            <Skeleton className="h-8 w-16" />
            <Skeleton className="h-8 w-16" />
          </div>
        </TableCell>
      </TableRow>
    ));
  };

  if (!isAdmin) {
    console.log("Not an admin user, redirecting to home");
    navigate('/questionnaire');
    return null;
  }

  return (
    <div>
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>All Questionnaires</CardTitle>
              <CardDescription>
                View all questionnaires from all users (both complete and incomplete)
              </CardDescription>
            </div>
            <div className="flex space-x-2">
              <Button 
                variant="default" 
                size="sm"
                onClick={() => navigate('/questionnaire/admin/templates')}
              >
                Edit Questionnaire
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleRefresh}
                disabled={loading}
              >
                {loading ? 'Refreshing...' : 'Refresh Data'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="mb-4">
              <p className="text-sm text-muted-foreground">
                Total: {loading ? <Skeleton as="span" className="inline-block h-4 w-12" /> : `${allQuestionnaires.length} questionnaires 
                (${allQuestionnaires.filter(q => q.isComplete).length} completed, ${allQuestionnaires.filter(q => !q.isComplete).length} incomplete)`}
              </p>
            </div>
            <div className="w-full">
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableHeader 
                      isSorted={sortField === 'status' ? sortDirection : false}
                      onSort={() => handleSort('status')}
                    >
                      Progress
                    </SortableHeader>
                    <SortableHeader 
                      isSorted={sortField === 'date' ? sortDirection : false}
                      onSort={() => handleSort('date')}
                    >
                      Date
                    </SortableHeader>
                    <SortableHeader 
                      isSorted={sortField === 'name' ? sortDirection : false}
                      onSort={() => handleSort('name')}
                    >
                      Name
                    </SortableHeader>
                    <SortableHeader 
                      isSorted={sortField === 'email' ? sortDirection : false}
                      onSort={() => handleSort('email')}
                    >
                      Email
                    </SortableHeader>
                    <SortableHeader 
                      isSorted={sortField === 'company' ? sortDirection : false}
                      onSort={() => handleSort('company')}
                    >
                      Company
                    </SortableHeader>
                    <SortableHeader 
                      isSorted={sortField === 'projectCategories' ? sortDirection : false}
                      onSort={() => handleSort('projectCategories')}
                    >
                      Project Categories
                    </SortableHeader>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    renderSkeletonRows()
                  ) : sortedQuestionnaires.length > 0 ? (
                    sortedQuestionnaires.map((questionnaire) => (
                      <TableRow 
                        key={questionnaire.id} 
                      >
                        <TableCell>
                          <div className="flex justify-center">
                            <CircularProgress 
                              percentage={getCompletionPercentage(questionnaire)}
                              isComplete={questionnaire.isComplete}
                            />
                          </div>
                        </TableCell>
                        <TableCell>
                          {formatDate(questionnaire.submittedAt || questionnaire.updatedAt || questionnaire.createdAt)}
                        </TableCell>
                        <TableCell>{questionnaire.formData.name || 'N/A'}</TableCell>
                        <TableCell>{questionnaire.formData.email || 'N/A'}</TableCell>
                        <TableCell>{questionnaire.formData.company || 'N/A'}</TableCell>
                        <TableCell>
                          {Array.isArray(questionnaire.formData.projectCategories) && questionnaire.formData.projectCategories.length > 0 
                            ? questionnaire.formData.projectCategories.join(', ') 
                            : questionnaire.formData.projectType || 'N/A'}
                        </TableCell>
                        <TableCell>
                          <div className="flex space-x-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => viewQuestionnaireDetails(questionnaire.userId, questionnaire.id)}
                            >
                              View
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleDeleteQuestionnaire(questionnaire.userId, questionnaire.id)}
                              disabled={deletingId === questionnaire.id}
                              className="text-red-500 hover:text-red-700"
                            >
                              {deletingId === questionnaire.id ? 'Deleting...' : 'Delete'}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8">
                        No questionnaires found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminQuestionnaires; 